import {
  AutocompleteInputComponent,
  AutocompleteItem,
  StateAutocompleteInput,
} from '@delmonicos/shared-design-system-react/src/components//autocomplete-input/autocomplete-input.component';
import { DatePickerInput } from '@delmonicos/shared-design-system-react/src/components/date-picker-input/date-picker-input';
import { ModeEnum } from '@delmonicos/shared-design-system-react/src/components/date-picker/date-picker';
import {
  Divider,
  DividerOrientation,
} from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import {
  PickerInput,
  PickerInputProps,
} from '@delmonicos/shared-design-system-react/src/components/picker-input/picker-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { endOfDay, formatISO, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  StatisticsAreaGranularityEnum,
  StatisticsTimeGranularityEnum,
} from '@model/enum/statistics-granularity.enum';
import { useCpmsBoGetChargePointsPaginatedQuery } from '@service/cpms-bo-charge-point.service';
import { useCpmsBoGetLocationsPaginatedQuery } from '@service/cpms-bo-location.service';
import { useCpmsBoGetNetworksPaginatedQuery } from '@service/cpms-bo-network.service';

import { useFilters } from '../context/filters.context';
import * as css from '../statistics.module.scss';

export const FiltersSectionComponent = (): React.JSX.Element => {
  const { t } = useTranslation();
  const [
    statisticsGranularityAreaSearchText,
    setStatisticsGranularityAreaSearchText,
  ] = useState<string>('');
  const { filters, setFilters } = useFilters();
  const {
    areaGranularity,
    selectedAreaGranularity,
    selectedTimeGranularity,
    timeGranularity,
  } = filters;

  const handleStatisticsAreaGranularityChange = (value?: string) => {
    if (!value) {
      setFilters({
        ...filters,
        areaGranularity: undefined,
        selectedAreaGranularity: null,
      });
    } else {
      setFilters({
        ...filters,
        areaGranularity: value as StatisticsAreaGranularityEnum,
        selectedAreaGranularity: null,
      });
    }
  };

  const handleStatisticsSelectedGranularityAreaChange = (
    item: AutocompleteItem | null,
  ) => {
    setFilters({
      ...filters,
      selectedAreaGranularity: item ?? null,
    });
  };

  const handleStatisticsTimeGranularityChange = (value?: string) => {
    if (!value) {
      setFilters({
        ...filters,
        timeGranularity: undefined,
        selectedTimeGranularity: undefined,
      });
    } else {
      setFilters({
        ...filters,
        timeGranularity: value as StatisticsTimeGranularityEnum,
        selectedTimeGranularity: undefined,
      });
    }
  };

  const handleStatisticsSelectedTimeGranularityChange: PickerInputProps['onChange'] =
    (dateRange) => {
      // TODO: Should be replaced by the “coerceDatePickerRangeRequiredSelection”
      // function once "PickerInput" is aligned with "DatePickerInput" in terms
      // of interface
      setFilters({
        ...filters,
        selectedTimeGranularity: {
          start: dateRange?.from
            ? formatISO(startOfDay(dateRange.from))
            : undefined,
          end: dateRange?.to ? formatISO(endOfDay(dateRange.to)) : undefined,
        },
      });
    };

  const { data: networks, isFetching: isFetchingNetworks } =
    useCpmsBoGetNetworksPaginatedQuery({
      limit: 10,
      offset: 0,
      name: `~${statisticsGranularityAreaSearchText}`,
    });

  const { data: locations, isFetching: isFetchingLocations } =
    useCpmsBoGetLocationsPaginatedQuery({
      limit: 10,
      offset: 0,
      name: `~${statisticsGranularityAreaSearchText}`,
    });

  const { data: chargePoints, isFetching: isFetchingChargePoints } =
    useCpmsBoGetChargePointsPaginatedQuery({
      limit: 10,
      offset: 0,
      name: `~${statisticsGranularityAreaSearchText}`,
    });

  const statisticsGranularityAreas: AutocompleteItem[] = useMemo(() => {
    switch (areaGranularity) {
      case StatisticsAreaGranularityEnum.Network:
        return (
          networks?.docs?.map((network) => ({
            key: network.id,
            label: network.name,
          })) ?? []
        );
      case StatisticsAreaGranularityEnum.Location:
        return (
          locations?.docs?.map((location) => ({
            key: location.id,
            label: location.name,
          })) ?? []
        );
      case StatisticsAreaGranularityEnum.ChargePoint:
        return (
          chargePoints?.docs?.map((chargePoint) => ({
            key: chargePoint.id,
            label: chargePoint.name,
          })) ?? []
        );
      default:
        return [];
    }
  }, [areaGranularity, chargePoints, locations, networks]);

  return (
    <Stack direction="row" gap={GapEnum.M} flex={1}>
      <Stack
        className={css.flexChildren}
        direction="row"
        flex={1}
        gap={GapEnum.M}>
        <Dropdown
          className={css.flex}
          withStyle
          removable
          placeholder={t(`$.statistics.granularity-area.placeholder`)}
          value={areaGranularity}
          onChange={handleStatisticsAreaGranularityChange}>
          {Object.values(StatisticsAreaGranularityEnum).map((value) => (
            <Dropdown.Option key={value} value={value}>
              {t(`$.statistics.granularity-area.${value}`)}
            </Dropdown.Option>
          ))}
        </Dropdown>
        <AutocompleteInputComponent
          className={css.flex}
          value={statisticsGranularityAreaSearchText}
          selectedItem={selectedAreaGranularity}
          onChange={setStatisticsGranularityAreaSearchText}
          onSearch={setStatisticsGranularityAreaSearchText}
          onSelect={handleStatisticsSelectedGranularityAreaChange}
          items={statisticsGranularityAreas}
          disabled={!areaGranularity}
          emptyResultText={t('global.empty-result')}
          state={
            isFetchingLocations || isFetchingNetworks || isFetchingChargePoints
              ? StateAutocompleteInput.LOADING
              : StateAutocompleteInput.DEFAULT
          }
        />
      </Stack>
      <Divider orientation={DividerOrientation.Vertical} />
      <Stack
        className={css.flexChildren}
        direction="row"
        flex={1}
        gap={GapEnum.M}>
        <Dropdown
          className={css.flex}
          withStyle
          removable
          placeholder={t(`$.statistics.granularity-time.placeholder`)}
          value={timeGranularity}
          onChange={handleStatisticsTimeGranularityChange}>
          {Object.values(StatisticsTimeGranularityEnum).map((value) => (
            <Dropdown.Option key={value} value={value}>
              {t(`$.statistics.granularity-time.${value}`)}
            </Dropdown.Option>
          ))}
        </Dropdown>
        {!timeGranularity ||
        timeGranularity === StatisticsTimeGranularityEnum.Month ? (
          <PickerInput
            disabled={!timeGranularity}
            mode="month"
            onChange={handleStatisticsSelectedTimeGranularityChange}
            selected={{
              from: selectedTimeGranularity?.start
                ? new Date(selectedTimeGranularity?.start)
                : undefined,
              to: selectedTimeGranularity?.end
                ? new Date(selectedTimeGranularity?.end)
                : undefined,
            }}
          />
        ) : timeGranularity === StatisticsTimeGranularityEnum.Year ? (
          <PickerInput
            disabled={!timeGranularity}
            mode="year"
            onChange={handleStatisticsSelectedTimeGranularityChange}
            selected={{
              from: selectedTimeGranularity?.start
                ? new Date(selectedTimeGranularity?.start)
                : undefined,
              to: selectedTimeGranularity?.end
                ? new Date(selectedTimeGranularity?.end)
                : undefined,
            }}
          />
        ) : timeGranularity === StatisticsTimeGranularityEnum.Quarter ? (
          <PickerInput
            disabled={!timeGranularity}
            mode="quarter"
            onChange={handleStatisticsSelectedTimeGranularityChange}
            selected={{
              from: selectedTimeGranularity?.start
                ? new Date(selectedTimeGranularity?.start)
                : undefined,
              to: selectedTimeGranularity?.end
                ? new Date(selectedTimeGranularity?.end)
                : undefined,
            }}
          />
        ) : (
          <DatePickerInput
            mode={ModeEnum.Range}
            selected={{
              from: selectedTimeGranularity?.start
                ? new Date(selectedTimeGranularity?.start)
                : undefined,
              to: selectedTimeGranularity?.end
                ? new Date(selectedTimeGranularity?.end)
                : undefined,
            }}
            hasLeftIcon={false}
            onSelect={handleStatisticsSelectedTimeGranularityChange}
          />
        )}
      </Stack>
    </Stack>
  );
};
